export type GameStoryFeature = {
  title: string;
  area:
    | "Top bar"
    | "Review workspace"
    | "Video"
    | "Patterns"
    | "Moments"
    | "Experiments"
    | "AI"
    | "Export"
    | "Setup";
  howToOpen: string;
  relevantWhen: string;
  source?: string;
  launchKey?: string;
};

// Maintained feature inventory for Pickleball Game Explorer.
// When a user-facing feature is added, removed, renamed, or materially changed,
// update this list so the in-app Features dialog stays accurate.
export const GAME_STORY_FEATURES: GameStoryFeature[] = [
  {
    title: "Game/package selector",
    area: "Top bar",
    howToOpen: "Use the game dropdown at the top left.",
    relevantWhen:
      "Switching between sample games or reviewing a different loaded package.",
    source: "App.tsx",
  },
  {
    title: "3-Min Triage",
    area: "Top bar",
    howToOpen: "Top bar -> 3-Min Triage (WIP).",
    relevantWhen:
      "You want a quick player-oriented review of points allowed, points scored, lost opportunities, and examples to watch.",
    source: "GameStory_ThreeMinuteReviewPopup.tsx",
    launchKey: "quick-triage",
  },
  {
    title: "Player Lens Review",
    area: "Top bar",
    howToOpen: "Top bar -> Player Lens Review (WIP).",
    relevantWhen:
      "You want to pick a player and review what's working, opportunities, mistakes, and repeated patterns.",
    source: "GameStory_PlayerLensReviewPopup.tsx",
    launchKey: "player-lens",
  },
  {
    title: "Rally story strip",
    area: "Review workspace",
    howToOpen: "Main Game Story view, above the active rally video.",
    relevantWhen:
      "Jumping between rallies and scanning score, serving team, rally length, quality, and notable moments.",
    source: "GameStoryDialog_UPDATED.tsx",
  },
  {
    title: "Reasons for losing rally",
    area: "Review workspace",
    howToOpen: "Main Game Story view, right side reason tables.",
    relevantWhen:
      "Filtering the match by forced faults, hit outs, missed thirds, missed returns, missed serves, net/tape, popups exploited, and unforced errors.",
    source: "GameStory_ReasonsLostEtc.tsx",
  },
  {
    title: "Player focus filter",
    area: "Review workspace",
    howToOpen: "Main Game Story view -> Player focus buttons above the reason tables.",
    relevantWhen:
      "Narrowing reason tables and matching clips to one player before inspecting a specific reason.",
    source: "GameStoryDialog_UPDATED.tsx",
  },
  {
    title: "Active rally video",
    area: "Video",
    howToOpen: "Main Game Story view -> active rally video panel.",
    relevantWhen:
      "Reviewing the currently selected rally with shot labels, playback controls, timeline marks, popout, bookmarks, AI entry points, and exports.",
    source: "ActiveRallyVideo.tsx",
  },
  {
    title: "Shot Target Lab",
    area: "Video",
    howToOpen: "Active rally video -> Shot Target Lab.",
    relevantWhen:
      "Exploring possible landing targets and trajectory options for a selected shot in video or 3D views.",
    source: "GameStory_ShotUniversePopup.tsx",
  },
  {
    title: "Opportunity Finder",
    area: "Video",
    howToOpen: "Main Game Story view -> Opportunity Finder below the active rally video.",
    relevantWhen:
      "Scanning the match for target-choice moments, then opening a candidate into Shot Target Lab to compare the real rally video with possible target lanes.",
    source: "GameStory_OpportunityFinderPopup.tsx",
    launchKey: "opportunity-finder",
  },
  {
    title: "Shot bookmarks",
    area: "Video",
    howToOpen: "Active rally video -> Bookmark or Bookmarks.",
    relevantWhen:
      "Marking and returning to specific shots while reviewing a rally.",
    source: "ShotBookmarksPopup.tsx",
  },
  {
    title: "Popout rally video",
    area: "Video",
    howToOpen: "Active rally video -> popout icon.",
    relevantWhen:
      "Viewing the rally larger while keeping the same rally context and controls.",
    source: "ActiveRallyVideo.tsx",
  },
  {
    title: "Next Ball Reads",
    area: "Video",
    howToOpen: "Active rally video -> Next Ball Reads.",
    relevantWhen:
      "Training anticipation from a data-selected shot: open Next Ball Reads, make the next-ball read in your head, reveal what happened, and optionally watch the same read again in the larger video popout.",
    source: "ActiveRallyVideo.tsx",
    launchKey: "next-ball-reads",
  },
  {
    title: "Pounce Opportunities",
    area: "Patterns",
    howToOpen: "Patterns toolbar -> Pounce Opportunities.",
    relevantWhen:
      "Reviewing readiness and anticipation windows where a player may have had a chance to attack.",
    source: "GameStory_pounceWindows.tsx",
    launchKey: "pounce",
  },
  {
    title: "3rd Shot Locations",
    area: "Patterns",
    howToOpen: "Patterns toolbar -> 3rd Shot Locations.",
    relevantWhen:
      "Comparing 3rd-shot contact locations, outcomes, and player-specific patterns.",
    source: "GameStory_3rdShotPopup_only_updated_v2_Mar_18_2026.tsx",
    launchKey: "third-shot",
  },
  {
    title: "4th Shot Locations",
    area: "Patterns",
    howToOpen: "Patterns toolbar -> 4th Shot Locations.",
    relevantWhen:
      "Seeing where fourth shots are contacted and how location relates to rally outcomes.",
    source: "GameStory_4thShotPopup_Mar_18_2026.tsx",
    launchKey: "fourth-shot",
  },
  {
    title: "5th Shot Locations",
    area: "Patterns",
    howToOpen: "Patterns toolbar -> 5th Shot Locations.",
    relevantWhen:
      "Reviewing fifth-shot location patterns after serve/return/third/fourth development.",
    source: "GameStory_5thShotPopup_Apr_23_2026.tsx",
    launchKey: "fifth-shot",
  },
  {
    title: "Dinks",
    area: "Patterns",
    howToOpen: "Patterns toolbar -> Dinks.",
    relevantWhen:
      "Isolating longer kitchen exchanges and inspecting who changes the rally first.",
    source: "GameStory_DinkRallies.tsx",
    launchKey: "dinks",
  },
  {
    title: "Serve Locations",
    area: "Patterns",
    howToOpen: "Patterns toolbar -> Serve Locations.",
    relevantWhen:
      "Comparing serve contact and return contact patterns by team/player.",
    source: "GameStory_ServeReturnPopups_New_New_Mar_18_2026.tsx",
    launchKey: "serve",
  },
  {
    title: "Return Locations",
    area: "Patterns",
    howToOpen: "Patterns toolbar -> Return Locations.",
    relevantWhen:
      "Comparing return contact and third-shot contact patterns by team/player.",
    source: "GameStory_ServeReturnPopups_New_New_Mar_18_2026.tsx",
    launchKey: "return",
  },
  {
    title: "Shot Quality",
    area: "Moments",
    howToOpen: "Moments toolbar -> Shot Quality.",
    relevantWhen:
      "Reviewing shot-level quality swings, turning points, and associated rally video.",
    source: "GameStory_shotQuality.tsx",
    launchKey: "shot-quality",
  },
  {
    title: "Rally Quality",
    area: "Moments",
    howToOpen: "Moments toolbar -> Rally Quality.",
    relevantWhen:
      "Scanning rally-by-rally quality charts and jumping into evidence clips.",
    source: "GameStory_RallyQualityOverview.tsx",
    launchKey: "rally-quality",
  },
  {
    title: "Fast shots",
    area: "Moments",
    howToOpen: "Moments toolbar -> Fast shots.",
    relevantWhen:
      "Finding and reviewing shots above the shared fast-shot MPH threshold.",
    source: "GameStory_FastShotsPopup.tsx",
    launchKey: "fast-shots",
  },
  {
    title: "Paddle Readiness Review",
    area: "Moments",
    howToOpen: "Moments toolbar -> Paddle Readiness.",
    relevantWhen:
      "Manually checking where a reviewed player's paddle was when the opponent contacted the ball, with game-stage filters based on shot number and court position. No paddle detection or readiness scoring is performed.",
    source: "GameStory_CoachViewPopups.tsx",
    launchKey: "paddle-readiness",
  },
  {
    title: "Left-Side Coverage Review",
    area: "Moments",
    howToOpen: "Moments toolbar -> Left Coverage.",
    relevantWhen:
      "Reviewing observable middle-take, partner-side coverage, and poach/reach candidates for the left-side player. The feature avoids intent or willingness language.",
    source: "GameStory_CoachViewPopups.tsx",
    launchKey: "left-side-coverage",
  },
  {
    title: "Candidate Speedups",
    area: "Moments",
    howToOpen: "Moments toolbar -> Speedups.",
    relevantWhen:
      "Reviewing possible kitchen-exchange speedup opportunities from PB Vision labels plus simple heuristics. Current candidates exclude 3rds, deep/baseline shots, ATPs, dinks, and drops, and require the next opponent touch to be a volley or no opponent touch before the point ends. Direction and intent are estimated, so clips are evidence to inspect rather than confirmed speedups.",
    source: "GameStory_CoachViewPopups.tsx",
    launchKey: "candidate-speedups",
  },
  {
    title: "Possible Popups",
    area: "Moments",
    howToOpen: "Moments toolbar -> Possible Popups.",
    relevantWhen:
      "Review dinks, drops, resets, blocks, and defensive replies whose measured trajectory may have sat up. Separate established kitchen exchanges from situations where one side is still transitioning.",
    source: "GameStory_CoachViewPopups.tsx",
    launchKey: "possible-popups",
  },
  {
    title: "Serve / Return Outcome Review",
    area: "Moments",
    howToOpen: "Moments toolbar -> Serve/Return Risk.",
    relevantWhen:
      "Exploring serve and return outcomes by player with estimated pace, landing depth, and angle filters. The review compares selected-filter rally outcomes against the player's baseline, then links each aggregate back to clips.",
    source: "GameStory_ServeReturnPopups_New_New_Mar_18_2026.tsx",
    launchKey: "serve-return-risk",
  },
  {
    title: "Dink Focus",
    area: "Moments",
    howToOpen: "Moments toolbar -> Dink Focus.",
    relevantWhen:
      "Focusing on an individual player's dink moments, defaulting to PB Vision forehand dink labels while allowing other kitchen/dink shot labels to be selected.",
    source: "GameStory_CoachViewPopups.tsx",
    launchKey: "dink-readiness",
  },
  {
    title: "ATPs",
    area: "Moments",
    howToOpen: "Moments toolbar -> ATPs.",
    relevantWhen:
      "Finding around-the-post candidates from PB Vision flags or ball-path geometry; clips default to one shot before through one shot after.",
    source: "GameStory_ATPPopup.tsx",
    launchKey: "atps",
  },
  {
    title: "Fast reactions",
    area: "Moments",
    howToOpen: "Moments toolbar -> Fast reactions.",
    relevantWhen:
      "Reviewing quick response exchanges under the configured reaction threshold.",
    source: "GameStory_fastReactions.tsx",
    launchKey: "fast-reactions",
  },
  {
    title: "Firefights",
    area: "Moments",
    howToOpen: "Moments toolbar -> Firefights.",
    relevantWhen:
      "Finding multi-shot rapid exchanges and watching them in context.",
    source: "GameStory_firefights.tsx",
    launchKey: "firefights",
  },
  {
    title: "Positioning Patterns",
    area: "Experiments",
    howToOpen: "Other toolbar -> Experiments -> Positioning Patterns.",
    relevantWhen:
      "Exploring player positioning and spacing patterns across rally moments.",
    source: "GameStory_PositioningPatternsPopup.tsx",
    launchKey: "positioning-patterns",
  },
  {
    title: "Match Identity",
    area: "Experiments",
    howToOpen: "Other toolbar -> Experiments -> Match Identity.",
    relevantWhen:
      "Summarizing the character of the match and the patterns that shaped it.",
    source: "GameStory_MatchIdentityPopup.tsx",
    launchKey: "match-identity",
  },
  {
    title: "Rally Causality",
    area: "Experiments",
    howToOpen: "Other toolbar -> Experiments -> Rally Causality.",
    relevantWhen:
      "Inspecting a more explicit chain of causes and effects inside rallies.",
    source: "GameStory_RallyCausality_Popup.tsx",
    launchKey: "rally-causality",
  },
  {
    title: "Single Top Focus",
    area: "Experiments",
    howToOpen: "Other toolbar -> Experiments -> Single Top Focus.",
    relevantWhen:
      "Testing a single recommended focus area for the next review pass.",
    source: "GameStory_SingleTopFocusPopup.tsx",
    launchKey: "single-top-focus",
  },
  {
    title: "Trajectory Wall",
    area: "Experiments",
    howToOpen: "Other toolbar -> Experiments -> Trajectory Wall.",
    relevantWhen:
      "Viewing many rally trajectory snapshots and comparing ball paths at scale.",
    source: "RallyTrajectoryWallPopup.tsx",
    launchKey: "trajectory-wall",
  },
  {
    title: "Points Added Report",
    area: "Experiments",
    howToOpen: "Other toolbar -> Experiments -> Points Added Report.",
    relevantWhen:
      "Reviewing a points-added style breakdown and drilling into supporting shots.",
    source: "GameStory_PointsAddedPopup.tsx",
    launchKey: "points-added-report",
  },
  {
    title: "Ask AI about rally",
    area: "AI",
    howToOpen: "Active rally video -> Ask AI about Rally tab/button.",
    relevantWhen:
      "Asking a question about the selected rally while preserving rally and shot context.",
    source: "ActiveRallyVideo.tsx",
  },
  {
    title: "AI History",
    area: "AI",
    howToOpen: "Other toolbar -> AI History.",
    relevantWhen:
      "Revisiting saved per-rally AI turns and opening their referenced rallies.",
    source: "GameStory_AITurnsHistoryPopup.tsx",
    launchKey: "ai-history",
  },
  {
    title: "Full Game AI",
    area: "AI",
    howToOpen: "Other toolbar -> Full Game AI (WIP).",
    relevantWhen:
      "Asking or staging questions at the game level rather than a single rally.",
    source: "GameStory_GameAIPopup.tsx",
    launchKey: "full-game-ai",
  },
  {
    title: "Export self-contained HTML",
    area: "Export",
    howToOpen: "Active rally video -> HTML/export icon.",
    relevantWhen:
      "Sharing a rally clip with embedded review context as a standalone HTML file.",
    source: "ActiveRallyVideo.tsx",
  },
  {
    title: "Export clip for social media",
    area: "Export",
    howToOpen: "Active rally video -> Export clip for social media.",
    relevantWhen:
      "Creating a 9:16 vertical MP4 with app branding, optional caption text, and optional shot labels.",
    source: "ActiveRallyVideo.tsx",
  },
  {
    title: "Add your game",
    area: "Setup",
    howToOpen: "Top-level game/package area when available.",
    relevantWhen:
      "Explaining that beta sample inclusion currently happens by sending a PB Vision link or game video link for testing.",
    source: "App.tsx",
    launchKey: "add-your-game",
  },
];

export const GAME_STORY_FEATURES_UPDATED_AT = "2026-07-06";
